/**
 * ConfirmDialog — styled in-app modal that replaces window.confirm().
 *
 * Driven by editor store state (confirmState). Components anywhere in
 * the tree call `useEditorStore.getState().askConfirm({ title, message,
 * danger })` and await the boolean. This component just listens to the
 * store and renders the UI.
 *
 * UX: Esc dismisses (returns false), click outside the card dismisses,
 * Enter on the keyboard confirms (handy for keyboard-only users).
 * Danger styling (red confirm button) when opts.danger === true.
 */
import React, { useEffect, useRef } from 'react';
import { useEditorStore } from '../store/editor';

export function ConfirmDialog() {
  const state = useEditorStore((s) => s.confirmState);
  const respond = useEditorStore((s) => s.respondConfirm);

  const confirmBtnRef = useRef<HTMLButtonElement>(null);

  // Auto-focus the confirm button on open so Enter triggers it. For
  // dangerous actions with both buttons we focus CANCEL instead, so
  // an accidental Enter doesn't confirm a destructive operation.
  // Single-button alerts always focus the confirm button (it's the
  // only thing the user CAN do).
  useEffect(() => {
    if (!state.open) return;
    const t = setTimeout(() => {
      if (state.danger && !state.singleButton) {
        const cancel = document.querySelector<HTMLButtonElement>('[data-cd-cancel]');
        cancel?.focus();
      } else {
        confirmBtnRef.current?.focus();
      }
    }, 30);
    return () => clearTimeout(t);
  }, [state.open, state.danger, state.singleButton]);

  // Esc → cancel; Enter → confirm (when not on cancel button).
  useEffect(() => {
    if (!state.open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        respond(false);
      } else if (e.key === 'Enter') {
        // If focus is on the cancel button, let it do its thing.
        const active = document.activeElement;
        if (active && active.getAttribute('data-cd-cancel') === '1') return;
        e.preventDefault();
        respond(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [state.open, respond]);

  if (!state.open) return null;

  // Pick sensible default labels based on intent. Single-button
  // (alert-style) dialogs get an "OK" confirm and no cancel.
  const isAlert = state.singleButton === true;
  const confirmLabel =
    state.confirmLabel ??
    (isAlert ? 'OK' : state.danger ? 'Delete' : 'Confirm');
  const cancelLabel = state.cancelLabel ?? 'Cancel';

  // Icon palette per tone. Danger > warning > info > none. The
  // colours pull from the design tokens so dark mode just works.
  const iconSpec =
    state.danger
      ? { bg: 'var(--danger-50)', fg: 'var(--danger-700)', glyph: '!' }
      : state.tone === 'warning'
      ? { bg: 'var(--warning-50)', fg: 'var(--warning-700)', glyph: '!' }
      : state.tone === 'info'
      ? { bg: 'var(--info-50)', fg: 'var(--info-700)', glyph: 'i' }
      : null;

  return (
    <div
      className="modal-backdrop"
      onClick={() => respond(false)}
    >
      <div
        className="modal-card"
        onClick={(e) => e.stopPropagation()}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="sld-cd-title"
      >
        <div
          id="sld-cd-title"
          style={{
            fontSize: 'var(--fs-xl)',
            fontWeight: 700,
            color: 'var(--text)',
            letterSpacing: '-0.01em',
            marginBottom: 'var(--space-2)',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}
        >
          {iconSpec && (
            <span
              aria-hidden
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 28,
                height: 28,
                borderRadius: '50%',
                background: iconSpec.bg,
                color: iconSpec.fg,
                fontSize: 14,
                fontWeight: 700,
                fontFamily:
                  iconSpec.glyph === 'i'
                    ? 'Georgia, serif'
                    : 'inherit',
                fontStyle: iconSpec.glyph === 'i' ? 'italic' : 'normal',
                flexShrink: 0,
              }}
            >
              {iconSpec.glyph}
            </span>
          )}
          {state.title}
        </div>
        <div
          style={{
            fontSize: 'var(--fs-md)',
            color: 'var(--text-muted)',
            lineHeight: 'var(--lh-base)',
            marginBottom: 'var(--space-5)',
            whiteSpace: 'pre-wrap',
          }}
        >
          {state.message}
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 'var(--space-2)',
          }}
        >
          {!isAlert && (
            <button
              type="button"
              data-cd-cancel="1"
              className="btn-secondary"
              onClick={() => respond(false)}
            >
              {cancelLabel}
            </button>
          )}
          <button
            ref={confirmBtnRef}
            type="button"
            className={state.danger ? 'btn-danger' : 'btn-primary'}
            onClick={() => respond(true)}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
